import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(response.data);
          return;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white border border-slate-200 p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-brand animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">{t('payment.verifying')}</h1>
            <p className="text-slate-500">{t('payment.pleaseWait')}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">{t('payment.success')}</h1>
            <p className="text-slate-500 mb-6">{t('payment.successDesc')}</p>
            {paymentData && (
              <div className="bg-slate-50 p-4 rounded-sm mb-6">
                <p className="text-sm text-slate-500">{t('payment.amountPaid')}</p>
                <p className="font-heading text-3xl font-bold text-brand">{paymentData.amount}€</p>
              </div>
            )}
            <div className="space-y-3">
              <Link to="/dashboard/registrations">
                <Button className="w-full btn-primary">{t('payment.viewRegistrations')}</Button>
              </Link>
              <Link to="/events">
                <Button variant="outline" className="w-full">{t('payment.exploreMore')}</Button>
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">{t('payment.error')}</h1>
            <p className="text-slate-500 mb-6">{t('payment.errorDesc')}</p>
            <Link to="/events">
              <Button className="w-full btn-primary">{t('payment.backToEvents')}</Button>
            </Link>
          </>
        )}

        {status === 'expired' && (
          <>
            <XCircle className="w-16 h-16 text-orange-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">{t('payment.expired')}</h1>
            <p className="text-slate-500 mb-6">{t('payment.expiredDesc')}</p>
            <Link to="/events">
              <Button className="w-full btn-primary">{t('payment.backToEvents')}</Button>
            </Link>
          </>
        )}

        {status === 'timeout' && (
          <>
            <Loader2 className="w-16 h-16 text-orange-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold mb-2">{t('payment.timeout')}</h1>
            <p className="text-slate-500 mb-6">{t('payment.timeoutDesc')}</p>
            <Link to="/dashboard/registrations">
              <Button className="w-full btn-primary">{t('payment.viewRegistrations')}</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
